import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { show as showExternalLinkModal } from '@/components/external-link-modal'
import Icon from '@/components/icon'
import { show as showVideoLightBox } from '@/components/video-light-box'
import { VideoLinkPlayer } from '@/components/video-player'
import { colors, fontSizes, fontWeights, breakpoints } from '@/styles'
import embeddedLinkGenerators from '@/utils/video-embedded-links'

const VideoWrapper = styled.div`
  position: static;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }
`

const VideoProcessingNote = styled.p`
  font-size: 12px;
  color: ${colors.regular};
  text-align: center;
  line-height: 15px;
  margin: 0 0 20px;
`

const ButtonsWrapper = styled.div`
  height: 54px;
  width: 100%;
  display: flex;
  align-items: center;
  background-color: #f2f6fa;
  border-radius: 6px;
  border: none;
  color: ${colors.link};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  line-height: 20px;
  margin: 10px 0;
  cursor: pointer;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    border-radius: 0;
    margin: 0;
    height: 54px;
  }
`
const Button = styled.div`
  height: 100%;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  .wrapper {
    border-right: 2px solid ${colors.link};
  }

  &:last-child .wrapper {
    border-right: none;
  }
`

const ButtonContentWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
  margin: auto 0;
  svg {
    margin-right: 5px;

    g {
      stroke: ${colors.link};
      path {
        fill: ${colors.link};
      }
    }
  }
`
const ActionButton = ({ icon, onClick, disabled, children }) => (
  <Button disabled={disabled} onClick={onClick}>
    <ButtonContentWrapper className="wrapper">
      <Icon id={icon} height={16} width={18} />
      {children}
    </ButtonContentWrapper>
  </Button>
)

const ShowingsButtonsBar = ({ unitVideos, showProcessingText, videoLinks = {} }) => {
  const { t } = useTranslation()
  const videoTourLink = videoLinks['tour_video']
  const threeDVideoLink = videoLinks['tour_3d']

  const playbackUrl = videoTourLink?.link_url //only 2d videos play internally, don't need to check other links
    ? embeddedLinkGenerators[videoTourLink.link_service](videoTourLink.link_url)
    : ''

  const openLinkExternally = () => {
    showExternalLinkModal({
      url: threeDVideoLink.link_url, //only 3d videos open externally, don't need to use other link
      message: t(
        'b.listing.edit.video_link.external_website.message',
        'Please note you will be redirected to a third-party website.'
      )
    })
  }

  const playVideoEmbedded = e => {
    showVideoLightBox({
      videoLocation: playbackUrl,
      controls: true,
      autoPlay: true,
      PlayerComponent: VideoLinkPlayer
    })
    e.stopPropagation()
  }

  const renderVideoButton = () => (
    <ActionButton
      disabled={showProcessingText}
      onClick={() =>
        showVideoLightBox({
          videoLocation: unitVideos[0].location,
          controls: true,
          autoPlay: true
        })
      }
      icon="play_video"
    >
      {showProcessingText
        ? `${t('b.listing.edit.processing.text', 'Processing')}...`
        : t('b.listing.view.watch_video.button', 'Watch video')}
    </ActionButton>
  )

  return (
    <VideoWrapper>
      <ButtonsWrapper>
        {unitVideos?.length > 0 && renderVideoButton()}
        {videoTourLink?.link_url && (
          <ActionButton icon="play_video" onClick={playVideoEmbedded}>
            {t('b.listing.view.watch_video.button', 'Watch video')}
          </ActionButton>
        )}
        {threeDVideoLink?.link_url && (
          <ActionButton icon="threeD_video" onClick={openLinkExternally}>
            {t('b.listing.view.3d_vr.button', '3D VR')}
          </ActionButton>
        )}
      </ButtonsWrapper>
      {showProcessingText && (
        <VideoProcessingNote>
          {t(
            'b.listing.view.video_processing.text',
            'Recently uploaded videos may take up to 10 minutes to be processed and displayed'
          )}
        </VideoProcessingNote>
      )}
    </VideoWrapper>
  )
}

export default ShowingsButtonsBar
